import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type EdgeTypes,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/base.css';
import { Maximize2, Minus, Plus, ScanSearch, X } from 'lucide-react';
import { isAutomated, useReducedMotion } from '@ai-tutor/ui';
import type { MindmapNode as OutlineNode } from '@/lib/mindmap-markdown';
import { layoutRoadmap, relatedIds } from './roadmapLayout';
import { pruneForPreview } from './previewTree';
import type { MindmapLayout } from './sideTree';
import {
  FIT_ALL,
  usePreviewViewport,
  useRoadmapViewport,
} from './useRoadmapViewport';
import { measurePill } from './measureLabel';
import { useFontsReady } from '@/hooks/ui/useFontsReady';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { MindmapNodeComponent, type MindmapFlowNode } from './MindmapNode';
import { MindmapEdgeComponent, type MindmapFlowEdge } from './MindmapEdge';

/** The pill faces `measureText` has to measure. Both are requested by name:
 *  JetBrains Mono may not have been used anywhere on the page yet. */
const PILL_FONTS = [
  '600 14px "Inter Variable"',
  '500 12px "Inter Variable"',
  '12px "JetBrains Mono Variable"',
] as const;

/** Module scope: React Flow remounts every node if these change identity. */
const NODE_TYPES: NodeTypes = { mindmap: MindmapNodeComponent };
const EDGE_TYPES: EdgeTypes = { mindmap: MindmapEdgeComponent };

const CONTROL =
  'tap-target flex h-9 w-9 items-center justify-center rounded-full border border-foreground/10 bg-card text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground';

interface MindmapCanvasProps {
  tree: OutlineNode;
  mode: 'inline' | 'fullscreen';
  /** Omitted for a map with no fullscreen view (the build view's draft). */
  onToggleFullscreen?: () => void;
  /** The figure's accessible name. */
  label?: string;
  /** The lesson concept a node's full label names, if any. */
  conceptIdFor?: (label: string) => string | undefined;
  /** A concept node was clicked (or Enter was pressed on it). */
  onOpenConcept?: (conceptId: string) => void;
  /** Move the map to this concept's node and highlight its branch. */
  focusConceptId?: string | null;
  /** Drawn over the canvas (the concept panel). */
  aside?: ReactNode;
  /**
   * A still preview (the build view's draft outline): the same map, fitted
   * whole into its box, with no pan, zoom, clicks or controls. Nodes from
   * `PREVIEW_DEPTHS` down are not drawn; the lines to them fade out instead.
   */
  preview?: boolean;
}

/** In a preview, nodes this deep (key points) are only hinted at - or from
 *  one level up (concepts too) when drawing the concepts would leave the map
 *  too small to read in its box (`usePreviewViewport`). */
export const PREVIEW_DEPTHS = [3, 2] as const;

/**
 * The lesson mindmap as a roadmap (`roadmapLayout.ts`) drawn with React Flow,
 * in the marketing site's visual language. React Flow owns only the viewport;
 * positions are ours and nodes are plain DOM, so pills use the learning tokens
 * and follow dark mode.
 *
 * Scroll moves down the path (`panOnScroll`); pinch, Ctrl+wheel and the +/-
 * buttons zoom. The map opens width-fitted at the main node
 * (`useRoadmapViewport`); the Fit button frames the whole roadmap.
 *
 * Read-only by construction: nothing is draggable, connectable, selectable or
 * focusable, so the only interactions are the viewport and clicking a node,
 * which keeps the path down to its step and its own subtree at full strength
 * and dims the rest. Clicking it again, or the empty canvas, clears it.
 *
 * A node naming one of the lesson's concepts (`conceptIdFor`) also opens that
 * concept (`onOpenConcept`): it is a button, reachable with Tab and Enter, it
 * stays highlighted while open, and the concept panel is drawn over the map
 * (`aside`).
 */
export function MindmapCanvas(props: MindmapCanvasProps) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}

function Canvas({
  tree,
  mode,
  onToggleFullscreen,
  conceptIdFor,
  onOpenConcept,
  focusConceptId,
  aside,
  label = 'Lesson mindmap',
  preview = false,
}: MindmapCanvasProps) {
  const fontsReady = useFontsReady(PILL_FONTS);
  const animate = useEntranceOnce();
  const [picked, setPicked] = useState<string | null>(null);

  const layouts = useMemo(
    () => buildLayouts(tree, preview),
    // `fontsReady` re-measures with the loaded faces.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tree, preview, fontsReady]
  );
  const [pick, setPick] = useState(0);
  const layout = layouts[Math.min(pick, layouts.length - 1)];
  const related = useMemo(
    () => (picked ? relatedIds(layout, picked) : null),
    [layout, picked]
  );
  const opener = useConceptOpener(conceptIdFor, onOpenConcept);
  const ghostDepth = preview ? PREVIEW_DEPTHS[pick] : Infinity;
  const { nodes, edges } = useMemo(
    () => ({
      nodes: toFlowNodes(layout, { related, animate, opener, ghostDepth }),
      edges: toFlowEdges(layout, { related, animate, ghostDepth }),
    }),
    [layout, related, animate, opener, ghostDepth]
  );
  useFocusConcept(layout, focusConceptId, conceptIdFor, setPicked);
  const wrapRef = useRef<HTMLDivElement>(null);
  useCanvasViewport(wrapRef, { layout, layouts, preview, onPick: setPick });
  // Controls move left below `lg`, clear of the Ask button.
  const compact = useMediaQuery('(max-width: 1023px)') && mode !== 'fullscreen';

  return (
    <div
      ref={wrapRef}
      role='figure'
      aria-label={label}
      className='relative h-full w-full'
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        minZoom={0.15}
        maxZoom={2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        {...interaction(preview, setPicked)}
        proOptions={{ hideAttribution: true }}
        className={
          preview ? '!cursor-default !bg-transparent' : '!bg-transparent'
        }
      >
        {!preview && (
          <Panel
            position={compact ? 'bottom-left' : 'bottom-right'}
            className={compact ? 'flex flex-col gap-2' : 'flex gap-2'}
          >
            <Controls mode={mode} onToggleFullscreen={onToggleFullscreen} />
          </Panel>
        )}
      </ReactFlow>
      {aside}
    </div>
  );
}

/** Decided once: re-rendering (selection, fonts) must not replay the entrance. */
function useEntranceOnce(): boolean {
  const reduced = useReducedMotion();
  const [animate] = useState(() => !reduced && !isAutomated());
  return animate;
}

/** A live map: the whole outline. A preview: one compact layout per level of
 *  detail in `PREVIEW_DEPTHS`, most detailed first. */
function buildLayouts(tree: OutlineNode, preview: boolean) {
  if (!preview) return [layoutRoadmap(tree, measurePill)];
  return PREVIEW_DEPTHS.map(d =>
    layoutRoadmap(pruneForPreview(tree, d), measurePill)
  );
}

/** A live map opens at its main node and scrolls; a preview is fitted
 *  whole, at the most detailed layout that stays readable. */
function useCanvasViewport(
  wrapRef: React.RefObject<HTMLDivElement>,
  {
    layout,
    layouts,
    preview,
    onPick,
  }: {
    layout: MindmapLayout;
    layouts: MindmapLayout[];
    preview: boolean;
    onPick: (index: number) => void;
  }
) {
  useRoadmapViewport(wrapRef, layout, !preview);
  usePreviewViewport(wrapRef, layouts, {
    enabled: preview,
    onPick,
  });
}

/** A live map scrolls along its path and answers clicks; a preview does
 *  neither (nothing pans, zooms or scrolls the page from it). */
function interaction(
  preview: boolean,
  setPicked: (update: (cur: string | null) => string | null) => void
) {
  if (preview) {
    return {
      panOnScroll: false,
      panOnDrag: false,
      zoomOnPinch: false,
      zoomOnDoubleClick: false,
      zoomOnScroll: false,
      preventScrolling: false,
    };
  }
  return {
    panOnScroll: true,
    zoomOnScroll: false,
    onNodeClick: (_: unknown, node: MindmapFlowNode) =>
      clickNode(node, setPicked),
    onPaneClick: () => setPicked(() => null),
  };
}

/** A click opens a concept node (which stays highlighted) or toggles the
 *  highlight of any other node. */
function clickNode(
  node: MindmapFlowNode,
  setPicked: (update: (cur: string | null) => string | null) => void
) {
  node.data.onOpen?.();
  setPicked(cur => (cur === node.id && !node.data.onOpen ? null : node.id));
}

/** For each node label, how to open the concept it names (if any). */
function useConceptOpener(
  conceptIdFor: MindmapCanvasProps['conceptIdFor'],
  onOpenConcept: MindmapCanvasProps['onOpenConcept']
) {
  return useMemo(() => {
    if (!conceptIdFor || !onOpenConcept) return undefined;
    return (label: string) => {
      const id = conceptIdFor(label);
      return id ? () => onOpenConcept(id) : undefined;
    };
  }, [conceptIdFor, onOpenConcept]);
}

/** Half the concept panel's width (26rem + margin), in screen pixels. */
const PANEL_SHIFT_PX = 216;

/** When the panel moves to another concept of the lesson, bring its node
 *  into view and highlight its branch, at the current zoom; when the panel
 *  closes, clear the highlight. */
function useFocusConcept(
  layout: MindmapLayout,
  conceptId: string | null | undefined,
  conceptIdFor: MindmapCanvasProps['conceptIdFor'],
  setPicked: (id: string | null) => void
) {
  const flow = useReactFlow();
  const shown = useRef(false);
  useEffect(() => {
    // The panel closed: drop the highlight it left on the map.
    if (!conceptId) {
      if (shown.current) setPicked(null);
      shown.current = false;
      return;
    }
    if (!conceptIdFor) return;
    shown.current = true;
    const node = layout.nodes.find(
      n => conceptIdFor(n.fullLabel) === conceptId
    );
    if (!node) return;
    setPicked(node.id);
    // The panel covers the right of the canvas: centre the node in the
    // part still visible, left of it.
    const zoom = flow.getZoom();
    flow.setCenter(node.x + PANEL_SHIFT_PX / zoom, node.y, {
      zoom,
      duration: 300,
    });
  }, [conceptId, conceptIdFor, layout, flow, setPicked]);
}

function Controls({
  mode,
  onToggleFullscreen,
}: Pick<MindmapCanvasProps, 'mode' | 'onToggleFullscreen'>) {
  const flow = useReactFlow();
  return (
    <>
      <button
        type='button'
        aria-label='Zoom in'
        className={CONTROL}
        onClick={() => flow.zoomIn({ duration: 200 })}
      >
        <Plus className='h-4 w-4' />
      </button>
      <button
        type='button'
        aria-label='Zoom out'
        className={CONTROL}
        onClick={() => flow.zoomOut({ duration: 200 })}
      >
        <Minus className='h-4 w-4' />
      </button>
      <button
        type='button'
        aria-label='Fit mindmap to view'
        className={CONTROL}
        onClick={() => flow.fitView({ ...FIT_ALL, duration: 250 })}
      >
        <ScanSearch className='h-4 w-4' />
      </button>
      {onToggleFullscreen && (
        <button
          type='button'
          aria-label={
            mode === 'inline'
              ? 'Expand mindmap to fullscreen'
              : 'Exit mindmap fullscreen'
          }
          className={CONTROL}
          onClick={onToggleFullscreen}
        >
          {mode === 'inline' ? (
            <Maximize2 className='h-4 w-4' />
          ) : (
            <X className='h-4 w-4' />
          )}
        </button>
      )}
    </>
  );
}

function toFlowNodes(
  layout: MindmapLayout,
  {
    related,
    animate,
    opener,
    ghostDepth = Infinity,
  }: {
    related: Set<string> | null;
    animate: boolean;
    opener?: (label: string) => (() => void) | undefined;
    ghostDepth?: number;
  }
): MindmapFlowNode[] {
  const delays = ghostDepth < Infinity ? revealDelays(layout) : undefined;
  return layout.nodes.map(n => ({
    id: n.id,
    type: 'mindmap',
    position: { x: n.x - n.w / 2, y: n.y - n.h / 2 },
    width: n.w,
    height: n.h,
    draggable: false,
    selectable: false,
    focusable: false,
    data: {
      ...n,
      dimmed: !!related && !related.has(n.id),
      animate,
      ghost: n.depth >= ghostDepth,
      delay: delays?.get(n.id),
      onOpen: opener?.(n.fullLabel),
    },
  }));
}

function toFlowEdges(
  layout: MindmapLayout,
  {
    related,
    animate,
    ghostDepth = Infinity,
  }: { related: Set<string> | null; animate: boolean; ghostDepth?: number }
): MindmapFlowEdge[] {
  const delays = ghostDepth < Infinity ? revealDelays(layout) : undefined;
  return layout.edges.map(e => ({
    id: e.id,
    source: e.source,
    target: e.target,
    // Spine: one step's bottom to the next step's top. Tree, right side:
    // parent's right edge to child's left; mirrored on the left.
    sourceHandle: e.kind === 'spine' ? 's-bottom' : `s-${e.side}`,
    targetHandle:
      e.kind === 'spine'
        ? 't-top'
        : `t-${e.side === 'right' ? 'left' : 'right'}`,
    type: 'mindmap',
    selectable: false,
    focusable: false,
    data: {
      ...e,
      dimmed: !!related && !(related.has(e.source) && related.has(e.target)),
      animate,
      ghost: e.kind === 'tree' && e.depth >= ghostDepth,
      // A line draws just before the node it leads to appears.
      delay: delays && Math.max(0, (delays.get(e.target) ?? 0) - REVEAL_LEAD),
    },
  }));
}

/** A preview reveals its nodes one after another, this far apart (ms) -
 *  unhurried, since the coverage check it covers runs 9-20 s. */
const REVEAL_STEP = 220;
/** How far ahead of its node a line starts drawing (ms). */
const REVEAL_LEAD = 150;

/**
 * A preview's entrance order, so the outline reads as being pulled out one
 * idea at a time: the lesson, then each section followed by its concepts and
 * then the faint level, top to bottom. Node id -> delay in ms.
 */
function revealDelays(layout: MindmapLayout): Map<string, number> {
  const order = [...layout.nodes].sort(
    (a, b) => a.branch - b.branch || a.depth - b.depth || a.y - b.y
  );
  return new Map(order.map((n, i) => [n.id, i * REVEAL_STEP]));
}
